// ─── useWakeWord Hook ─────────────────────────────────────────────────────────
// Ascolto passivo continuo — attiva callback quando sente le wake phrases.
// Funziona su Chrome desktop e Android. Non funziona su Safari/iOS (limite Apple).
// Backoff progressivo sui restart Chrome: 300ms → 3s → 10s → 30s.
// Pausa automatica quando l'app va in background (Page Visibility API).

import { useState, useRef, useCallback, useEffect } from 'react';

const WAKE_PHRASES_IT = [
  'ehi sirus', 'hey sirus', 'ei sirus', 'ciao sirus',
  'ok sirus', 'sirus', 'ehi sirius', 'hey sirius',
];

const WAKE_PHRASES_EN = [
  'hey sirus', 'hi sirus', 'hello sirus', 'ok sirus', 'sirus',
];

const LANG_MAP = {
  it: 'it-IT', en: 'en-US', es: 'es-ES',
  fr: 'fr-FR', de: 'de-DE', pt: 'pt-PT', nl: 'nl-NL',
};
function toLangCode(lang) { return LANG_MAP[lang] || 'it-IT'; }

function getWakePhrases(lang) {
  return lang === 'en' ? WAKE_PHRASES_EN : WAKE_PHRASES_IT;
}

function matchesWakeWord(transcript, lang) {
  const t = transcript.toLowerCase().trim();
  return getWakePhrases(lang).some(p => t.includes(p));
}

// Delay crescente tra un restart e l'altro quando Chrome ferma la sessione
const BACKOFF_DELAYS = [300, 3000, 10000, 30000];

export function useWakeWord({ enabled, onWake, lang = 'it' }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const recRef     = useRef(null);
  const enabledRef = useRef(enabled);
  const onWakeRef  = useRef(onWake);
  const backoffRef = useRef(0);

  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  useEffect(() => { onWakeRef.current  = onWake;  }, [onWake]);

  const stop = useCallback(() => {
    if (recRef.current) {
      try { recRef.current.stop(); } catch {}
      recRef.current = null;
    }
    setListening(false);
  }, []);

  const start = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }

    const rec = new SR();
    rec.lang            = toLangCode(lang);
    rec.continuous      = true;
    rec.interimResults  = true;
    rec.maxAlternatives = 3;

    rec.onstart = () => setListening(true);

    rec.onend = () => {
      setListening(false);
      if (!enabledRef.current) return;
      // Non riavviare se l'app è in background — nessun suono inutile
      if (document.visibilityState === 'hidden') return;

      const idx   = Math.min(backoffRef.current, BACKOFF_DELAYS.length - 1);
      const delay = BACKOFF_DELAYS[idx];
      backoffRef.current = Math.min(backoffRef.current + 1, BACKOFF_DELAYS.length - 1);

      setTimeout(() => {
        if (enabledRef.current && document.visibilityState !== 'hidden') start();
      }, delay);
    };

    rec.onerror = (e) => {
      if (e.error === 'not-allowed') { setSupported(false); stop(); return; }
      setListening(false);
    };

    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const transcript = e.results[i][0].transcript;
        if (matchesWakeWord(transcript, lang)) {
          backoffRef.current = 0; // reset backoff dopo wake word riuscita
          rec.stop();
          onWakeRef.current?.();
          return;
        }
      }
    };

    try {
      rec.start();
      recRef.current = rec;
    } catch { setListening(false); }
  }, [lang, stop]);

  // Riprende da zero quando l'app torna in foreground
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && enabledRef.current) {
        backoffRef.current = 0;
        start();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [start]);

  // Avvia/ferma in base allo switch abilitato
  useEffect(() => {
    if (enabled) {
      backoffRef.current = 0;
      start();
    } else {
      stop();
    }
    return () => stop();
  }, [enabled, start, stop]);

  return { listening, supported };
}
